import create from "zustand";
import RbacApi from "api/Rbac/Rbac.api";
import { IRbacContext } from "api/Rbac/Rbac.types";

const VIEW_AS_KEY = "rbac_view_as";

function saveViewAsState(context: IRbacContext, original: IRbacContext) {
  sessionStorage.setItem(
    VIEW_AS_KEY,
    JSON.stringify({ context, originalContext: original })
  );
}

function loadViewAsState(): {
  context: IRbacContext;
  originalContext: IRbacContext;
} | null {
  try {
    const raw = sessionStorage.getItem(VIEW_AS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function clearViewAsState() {
  sessionStorage.removeItem(VIEW_AS_KEY);
}

interface RbacStoreState {
  context: IRbacContext | null;
  originalContext: IRbacContext | null;
  isViewingAs: boolean;
  loading: boolean;
  loaded: boolean;
  fetchContext: () => Promise<void>;
  hasPermission: (key: string) => boolean;
  hasAnyPermission: (keys: string[]) => boolean;
  getDataScopes: (scopeType: string) => string[];
  viewAsRole: (roleId: number) => Promise<boolean>;
  viewAsUser: (userId: number) => Promise<boolean>;
  exitViewAs: () => void;
}

const useRbacStore = create<RbacStoreState>((set, get) => ({
  context: null,
  originalContext: null,
  isViewingAs: false,
  loading: false,
  loaded: false,

  fetchContext: async () => {
    if (get().loading) return;

    // If we have a persisted view-as state, restore it instead of fetching
    const viewAsState = loadViewAsState();
    if (viewAsState) {
      set({
        context: viewAsState.context,
        originalContext: viewAsState.originalContext,
        isViewingAs: true,
        loading: false,
        loaded: true,
      });
      return;
    }

    set({ loading: true });
    const context = await RbacApi.getUserContext();
    set({ context, loading: false, loaded: true });
  },

  hasPermission: (key: string) => {
    const { context } = get();
    if (!context?.permissions) return false;
    return context.permissions.includes(key);
  },

  hasAnyPermission: (keys: string[]) => {
    const { context } = get();
    if (!context?.permissions) return false;
    return keys.some((key) => context.permissions.includes(key));
  },

  getDataScopes: (scopeType: string) => {
    const { context } = get();
    if (!context?.data_scopes) return [];
    return context.data_scopes[scopeType] || [];
  },

  viewAsRole: async (roleId: number) => {
    const currentContext = get().originalContext ?? get().context;
    const impersonatedContext = await RbacApi.viewAsRole(roleId);
    if (!impersonatedContext || !currentContext) return false;

    saveViewAsState(impersonatedContext, currentContext);
    set({
      originalContext: currentContext,
      context: impersonatedContext,
      isViewingAs: true,
    });
    return true;
  },

  viewAsUser: async (userId: number) => {
    const currentContext = get().originalContext ?? get().context;
    const impersonatedContext = await RbacApi.viewAsUser(userId);
    if (!impersonatedContext || !currentContext) return false;

    saveViewAsState(impersonatedContext, currentContext);
    set({
      originalContext: currentContext,
      context: impersonatedContext,
      isViewingAs: true,
    });
    return true;
  },

  exitViewAs: () => {
    const { originalContext } = get();
    clearViewAsState();
    if (originalContext) {
      set({
        context: originalContext,
        originalContext: null,
        isViewingAs: false,
      });
    }
  },
}));

export default useRbacStore;
